/**
 * useBranding* hooks — Wave 1c W1c-07.
 *
 * TanStack Query hooks wrapping the four branding endpoints:
 *
 *   GET    /api/v1/branding              → useBrandingQuery()
 *   PUT    /api/v1/branding              → useUpdateBranding()
 *   POST   /api/v1/branding/assets       → useUploadBrandingAsset()
 *   DELETE /api/v1/branding/assets/:id   → useDeleteBrandingAsset()
 *
 * The `useBranding()` named export (singular) lives in
 * `@/contexts/branding-context` and exposes the *applied* effective
 * branding for the running app — that one is the live theme + provider.
 * The hooks in this file own the editor's draft / save / upload flow
 * and intentionally do NOT mutate the BrandingProvider state directly;
 * the editor calls `updateBranding()` on the context after a successful
 * save, which fires the BroadcastChannel (W1c-08).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api-client';

// ─── Wire types (mirror packages/shared TenantBrandingSchema) ────────────

export interface BrandingColors {
  primary: string;
  primaryForeground: string;
  background: string;
  card: string;
  text: string;
  popover: string;
  sidebarBackground: string;
  sidebarText: string;
  sidebarActive: string;
  sidebarActiveText: string;
  sidebarHover: string;
}

export type BrandingDisplayMode = 'full' | 'both' | 'icon-name';

/** Full effective shape returned by GET /branding (resolved chain). */
export interface EffectiveBranding {
  useDefaultTheme: boolean;
  displayMode: BrandingDisplayMode;
  portalTitle: string;

  logoUrl: string | null;
  logoIconUrl: string | null;
  faviconUrl: string | null;
  loginBackgroundUrl: string | null;

  loginWelcomeText: string | null;
  loginSubtitle: string | null;
  footerText: string | null;

  light: BrandingColors;
  dark: BrandingColors;

  effectiveVersion: number;
  lockedFields: string[];
  source: 'tenant' | 'platform' | 'system_default';
}

export interface BrandingWarning {
  field: string;
  code: string;
  detail: string;
  ratio: number;
}

/** PUT /branding response — effective + optional warnings. */
export interface EffectiveBrandingWithWarnings extends EffectiveBranding {
  warnings?: BrandingWarning[];
}

/** Writable subset of EffectiveBranding (matches TenantBrandingWriteSchema). */
export interface BrandingWrite {
  useDefaultTheme?: boolean;
  displayMode?: BrandingDisplayMode;
  portalTitle?: string;
  logoFullAssetId?: string | null;
  logoIconAssetId?: string | null;
  faviconAssetId?: string | null;
  loginBackgroundAssetId?: string | null;
  loginWelcomeText?: string | null;
  loginSubtitle?: string | null;
  footerText?: string | null;
  light?: BrandingColors;
  dark?: BrandingColors;
  /** When true, server records that the user accepted the low-contrast warning. */
  acknowledgeLowContrast?: boolean;
}

export type BrandingAssetKind = 'logo_full' | 'logo_icon' | 'favicon' | 'login_background';

export interface BrandingAssetUploadResponse {
  id: string;
  kind: BrandingAssetKind;
  url: string;
  byteSize: number;
  contentType: string;
  uploadedAt: string;
  /** True when the upload deduplicated to an existing asset. */
  deduplicated: boolean;
}

/** Discriminated error from the API (mirrors ErrorEnvelopeSchema). */
export interface BrandingError extends Error {
  code: string;
  status: number;
  field?: string;
  lockedFields?: string[];
}

// ─── Query: GET /branding ────────────────────────────────────────────────

export const BRANDING_QUERY_KEY = ['branding'] as const;

export function useBrandingQuery(options?: { enabled?: boolean }) {
  return useQuery<EffectiveBranding, BrandingError>({
    queryKey: BRANDING_QUERY_KEY,
    queryFn: () => apiClient.get<EffectiveBranding>('/branding'),
    staleTime: 60_000, // 1 min — relatively static; BroadcastChannel + cache invalidation handle freshness
    enabled: options?.enabled ?? true,
  });
}

// ─── Mutation: PUT /branding ─────────────────────────────────────────────

export function useUpdateBranding() {
  const qc = useQueryClient();
  return useMutation<EffectiveBrandingWithWarnings, BrandingError, BrandingWrite>({
    mutationKey: ['branding-save'],
    mutationFn: async (body) => {
      try {
        return await apiClient.put<EffectiveBrandingWithWarnings>('/branding', body);
      } catch (err) {
        // apiClient throws Error with .status + .data attached — re-shape into BrandingError
        const branded = err as BrandingError;
        const data = (err as any).data ?? {};
        branded.code = data?.error?.code ?? `HTTP_${branded.status ?? 500}`;
        branded.field = data?.error?.field;
        branded.lockedFields = data?.error?.lockedFields;
        throw branded;
      }
    },
    onSuccess: (data) => {
      // Update the GET cache so the next render uses fresh data.
      qc.setQueryData<EffectiveBranding>(BRANDING_QUERY_KEY, {
        useDefaultTheme: data.useDefaultTheme,
        displayMode: data.displayMode,
        portalTitle: data.portalTitle,
        logoUrl: data.logoUrl,
        logoIconUrl: data.logoIconUrl,
        faviconUrl: data.faviconUrl,
        loginBackgroundUrl: data.loginBackgroundUrl,
        loginWelcomeText: data.loginWelcomeText,
        loginSubtitle: data.loginSubtitle,
        footerText: data.footerText,
        light: data.light,
        dark: data.dark,
        effectiveVersion: data.effectiveVersion,
        lockedFields: data.lockedFields,
        source: data.source,
      });
    },
  });
}

// ─── Mutation: POST /branding/assets (multipart) ─────────────────────────

export interface UploadBrandingAssetVars {
  kind: BrandingAssetKind;
  file: File;
}

export function useUploadBrandingAsset() {
  return useMutation<BrandingAssetUploadResponse, BrandingError, UploadBrandingAssetVars>({
    mutationKey: ['branding-asset-upload'],
    mutationFn: async ({ kind, file }) => {
      const form = new FormData();
      form.append('kind', kind);
      form.append('file', file, file.name);

      // apiClient.post sets Content-Type: application/json which kills
      // multipart boundary detection. Use raw fetch with the access token.
      const token = localStorage.getItem('accessToken');
      const url = `/api/v1/branding/assets`;
      const headers: Record<string, string> = {};
      if (token) headers.authorization = `Bearer ${token}`;

      const res = await fetch(url, { method: 'POST', headers, body: form });
      if (!res.ok) {
        let body: any = null;
        try {
          body = await res.json();
        } catch {
          /* ignore — non-JSON error body */
        }
        const err = new Error(body?.error?.message ?? `Upload failed (${res.status})`) as BrandingError;
        err.status = res.status;
        err.code = body?.error?.code ?? `HTTP_${res.status}`;
        err.field = body?.error?.field;
        throw err;
      }
      return (await res.json()) as BrandingAssetUploadResponse;
    },
  });
}

// ─── Mutation: DELETE /branding/assets/:id ───────────────────────────────

export function useDeleteBrandingAsset() {
  return useMutation<void, BrandingError, string>({
    mutationKey: ['branding-asset-delete'],
    mutationFn: async (assetId) => {
      try {
        await apiClient.delete<void>(`/branding/assets/${assetId}`);
      } catch (err) {
        const branded = err as BrandingError;
        const data = (err as any).data ?? {};
        branded.code = data?.error?.code ?? `HTTP_${branded.status ?? 500}`;
        throw branded;
      }
    },
  });
}
